import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  getSectionOgImageUrl,
} from "@config/ogImageConstants";
import {
  getAuthorsName,
  getChapter1,
  getContentCat,
  getLevel2,
  getTagsName,
} from "@elements/GAData/GADataHelper";
import { capitalize, truncateText } from "@helper/stringTransform";
import { dateFormatWithTz, formatDate } from "@helper/useDateFormat";
import type { ProcessedArticleData } from "@transformer/useOSResponse";
import type { GADataProps } from "@typings/GA";
import type { MetaTagsProps } from "@typings/MetaTags";
import type { ProcessedArticleBody } from "@typings/Opensearch";
import type { PageMetaDataValues } from "@typings/PageMetaData";

type ArticleMetaDataProps = {
  article: ProcessedArticleData;
  pageNumber: number;
  gsChannels: string[];
};

export default function ArticleMetaData({
  article,
  pageNumber,
  gsChannels,
}: ArticleMetaDataProps): PageMetaDataValues {
  const keywords = getTagsName(article.tags || []);
  const level2 = getLevel2(article);
  const metaTitle =
    level2 === "kaki makan" &&
    article.location?.title &&
    article.location.address
      ? `${article.location.title} Ulasan, Menu & Tempat Kaki Makan di ${article.location.address} - Beritaharian.sg`
      : article.seoFields?.metaTitle && article.seoFields.metaTitle !== ""
        ? article.seoFields.metaTitle
        : `${article.title}, Berita ${capitalize(level2)} - Beritaharian.sg`;
  const metaDescription =
    level2 === "kaki makan" &&
    article.location?.title &&
    article.location.address
      ? `Baca ulasan lengkap tentang ${article.location.title}, hidangan popular & suasana makan di ${article.location.address}. Panduan kaki makan Singapura oleh Berita Harian.`
      : article.seoFields?.metaDescription &&
          article.seoFields.metaDescription.trim() !== ""
        ? article.seoFields.metaDescription
        : `Berita - ${getFirstParagraph(article.articleBody)} Baca lebih lanjut di BeritaHarian.sg`;
  const metaRobots =
    article.seoFields?.metaRobots && article.seoFields.metaRobots == 1
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const gaData = {
    title: article.title,
    level2: level2,
    chapter1: getChapter1(article),
    contentcat: getContentCat(article.contentAccess),
    section: level2,
    contenttype: "1",
    pagination: pageNumber,
    author: getAuthorsName(article.authors || []),
    cue_articleid: article.id,
    articleid: article.id,
    keyword: keywords,
    pubdate: formatDate(article.datetime, "yyyy-MM-dd HH:mm:ss"),
    content_publication_date_utc: article.datetime,
    content_update_date_utc: article.dateEdited,
  } as GADataProps;
  const ogImageUrl = article.ogImageUrl || article.imageField?.url;
  // Articles without their own image fall back to the branded section image
  // rather than the legacy BH logo that getMetaData() would otherwise emit.
  const fallbackOgImageUrl = getSectionOgImageUrl(
    article.uniqueSectionName,
    article.parentSection,
  );

  const metaTags = {
    title: metaTitle,
    description: metaDescription,
    slug: article.urlPath,
    ogType: "Article",
    ogTitle: article.ogFields?.ogTitle || metaTitle,
    ogDescription: article.ogFields?.ogDescription || metaDescription,
    keywords: keywords.split(","),
    robotsContent: metaRobots,
    customCanonicalUrl: article.seoFields?.canonicalUrl,
    twitterCardContent: "summary",
    image: {
      url: article.imageField?.url
        ? `${article.imageField.url}?w=${DEFAULT_OG_IMAGE_WIDTH}`
        : fallbackOgImageUrl,
      width: DEFAULT_OG_IMAGE_WIDTH,
      height: DEFAULT_OG_IMAGE_HEIGHT,
    },
    ogImage: {
      url: ogImageUrl
        ? `${ogImageUrl}?w=${DEFAULT_OG_IMAGE_WIDTH}`
        : fallbackOgImageUrl,
      width: DEFAULT_OG_IMAGE_WIDTH,
      height: DEFAULT_OG_IMAGE_HEIGHT,
    },
    isArticlePage: true,
    publishedTime: formatDate(article.datetime, dateFormatWithTz),
    modifiedTime: formatDate(article.dateEdited, dateFormatWithTz),
    gsChannels,
  } as MetaTagsProps;

  return {
    gaData: gaData,
    metaTags: metaTags,
  };
}

export function getFirstParagraph(
  articleBody: ProcessedArticleBody | null,
  numberOfCharacters?: number,
): string {
  if (!articleBody) return "";
  const firstParagraph =
    articleBody.find((element) => element.type === "paragraph")?.value || "";
  return numberOfCharacters
    ? truncateText(firstParagraph, numberOfCharacters)
    : firstParagraph;
}
